import type { ConnectFourState, ConnectFourVisualColor } from "@topzle/game-connect-four";
import type { CSSProperties, ReactNode } from "react";

interface ConnectFourBoardProps {
  state: ConnectFourState;
  participantId: string;
  pending: boolean;
  serverError: string | null;
  playAgain: ReactNode;
  onDrop: (column: number) => void;
}

const rows = Array.from({ length: 6 }, (_, index) => index);
const columns = Array.from({ length: 7 }, (_, index) => index);
const colorPresentation: Record<
  ConnectFourVisualColor,
  { label: string; base: string; light: string; dark: string }
> = {
  blue: { label: "Blue", base: "#2f8cff", light: "#c7e2ff", dark: "#1451a4" },
  coral: { label: "Coral", base: "#ff647c", light: "#ffd2d9", dark: "#9f2539" },
  gold: { label: "Gold", base: "#ffc247", light: "#fff0bd", dark: "#a86408" },
  emerald: { label: "Emerald", base: "#2ed69b", light: "#c6ffea", dark: "#087b55" },
  cyan: { label: "Cyan", base: "#2ccaf5", light: "#c6f7ff", dark: "#087d9d" },
  orange: { label: "Orange", base: "#ff8a3d", light: "#ffe0c7", dark: "#a83e0a" },
};

export function ConnectFourBoard({
  state,
  participantId,
  pending,
  serverError,
  playAgain,
  onDrop,
}: ConnectFourBoardProps) {
  const humanDisc =
    state.players.red === participantId
      ? "red"
      : state.players.yellow === participantId
        ? "yellow"
        : null;
  const currentPlayerId = state.players[state.turn];
  const redColor = colorPresentation[state.colors?.red ?? "cyan"];
  const yellowColor = colorPresentation[state.colors?.yellow ?? "gold"];
  const colors = { red: redColor, yellow: yellowColor };
  const colorStyle = {
    "--connect-red": redColor.base,
    "--connect-red-light": redColor.light,
    "--connect-red-dark": redColor.dark,
    "--connect-yellow": yellowColor.base,
    "--connect-yellow-light": yellowColor.light,
    "--connect-yellow-dark": yellowColor.dark,
  } as CSSProperties;
  const humanTurn = state.status === "playing" && humanDisc === state.turn;
  const winningCells = new Set(state.winningCells ?? []);
  const status =
    state.status === "playing"
      ? humanTurn
        ? "Your turn · choose a column"
        : currentPlayerId.startsWith("ai:")
          ? "Topzle AI is lining up a drop"
          : `${colors[state.turn].label} is choosing`
      : state.status === "draw"
        ? "Board full · draw"
        : state.winner === humanDisc
          ? "Four connected · you win!"
          : `${state.winner ? colors[state.winner].label : "A player"} connected four`;

  return (
    <div className={`active-game connect-four-game turn-${state.turn}`} style={colorStyle}>
      <div className="game-status connect-four-status">
        <span>CONNECT 4 · ROUND {String(state.round).padStart(2, "0")}</span>
        <strong>{status}</strong>
      </div>
      <fieldset className="connect-four-player-key" aria-label="Player colors">
        <span className={state.turn === "red" && state.status === "playing" ? "active" : ""}>
          <i className="red" /> {state.players.red === participantId ? "You" : redColor.label}
        </span>
        <span className={state.turn === "yellow" && state.status === "playing" ? "active" : ""}>
          <i className="yellow" />{" "}
          {state.players.yellow === participantId
            ? "You"
            : state.players.yellow.startsWith("ai:")
              ? "Topzle AI"
              : yellowColor.label}
        </span>
        {pending && (
          <span className="connect-four-live-indicator" role="status" aria-label="Move in progress">
            <i />
            <i />
            <i />
          </span>
        )}
      </fieldset>
      <section className="connect-four-stage" aria-label="Connect 4 board">
        <div className="connect-four-board">
          {columns.map((column) => {
            const full = state.board[column] !== null;
            return (
              <button
                type="button"
                className="connect-four-column"
                aria-label={
                  full ? `Column ${column + 1} is full` : `Drop a disc in column ${column + 1}`
                }
                disabled={!humanTurn || pending || full}
                onClick={() => onDrop(column)}
                key={column}
              >
                <span className="connect-four-drop-preview" aria-hidden="true" />
                {rows.map((row) => {
                  const index = row * 7 + column;
                  const disc = state.board[index];
                  return (
                    <span
                      className={[
                        "connect-four-cell",
                        disc ?? "",
                        state.lastMove === index ? "last" : "",
                        winningCells.has(index) ? "winner" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      key={index}
                    >
                      {disc && <i aria-hidden="true" />}
                    </span>
                  );
                })}
              </button>
            );
          })}
        </div>
      </section>
      <div
        className={serverError ? "connect-four-feedback visible" : "connect-four-feedback"}
        role="status"
      >
        {serverError ?? " "}
      </div>
      {state.status !== "playing" && <div className="connect-four-actions">{playAgain}</div>}
    </div>
  );
}
